import { ref, isRef, computed, watch } from 'vue';
import { useCambioButacaZonas } from './useCambioButacaZonas';
import { useCambioButacaButacas } from './useCambioButacaButacas';
import { useCambioButacaConfirmacion } from './useCambioButacaConfirmacion';
import { useCambioButacaDispatcher } from './useCambioButacaDispatcher';
import abonosService from '@/services/perfil/abonos';

// ─── Constantes del stepper ────────────────────────────────────────────────
export const PASO_ZONA         = 1;
export const PASO_BUTACA       = 2;
export const PASO_CONFIRMACION = 3;
export const PASO_PAGO         = 4;
export const PASO_PAGO_CONFIRMACION = 5;

/**
 * useCambioButaca — Orquestador del flujo de cambio de asiento (butaca o plaza) online.
 *
 * Compone tres sub-composables especializados (Zonas, Asientos, Confirmación)
 * y gestiona la máquina de estados multi-paso definida por las constantes:
 * PASO_ZONA → PASO_BUTACA → PASO_CONFIRMACION → PASO_PAGO.
 *
 * El flujo es un wizard fijo de 4 pasos. El último paso (`PASO_PAGO`) sólo
 * se activa cuando el cambio requiere pasarela de pago con `jetId` presente
 * (Paycomet, devuelto por `reservar()`). El TPV Redsys (`formulario`) NO
 * avanza a PASO_PAGO: su form se auto-submitea desde el paso de confirmación
 * (CambioButacaPasoConfirmacion). El composable expone los datos necesarios
 * (`jetId`, `formulario`, `precioTotal`, etc.) para que la vista padre
 * construya el flujo de pago correspondiente.
 * Soporta universalmente butacas numeradas (Camino 1: region_id+row+seat) y
 * plazas (Camino 2: plaza_id) según la flag `tiene_plazas` de la zona seleccionada.
 * No existe paso de selección de tipo/descuento (esa lógica es configuración
 * del backoffice, no del frontend público). Tampoco se llama a GET /info durante
 * la inicialización (ese endpoint requiere session_id + tpv_order y es para
 * consultas post-operación).
 *
 * @param {import('vue').Ref<string|number>|string|number|function} abonadoIdSource      ID del abonado
 * @param {import('vue').Ref<string|number>|string|number|function} [listaAbonadoIdSource] ID de temporada (getListaAbonados.id)
 */
export function useCambioButaca(abonadoIdSource, listaAbonadoIdSource) {

    // ─── Helpers ──────────────────────────────────────────────────────────
    const getAbonadoId = () => {
        if (isRef(abonadoIdSource)) return abonadoIdSource.value;
        if (typeof abonadoIdSource === 'function') return abonadoIdSource();
        return abonadoIdSource;
    };

    /**
     * Resuelve el listaAbonadoId desde la fuente inyectada (ref/function/value).
     * @returns {string|number|null}
     */
    const getListaAbonadoId = () => {
        if (!listaAbonadoIdSource) return null;
        if (isRef(listaAbonadoIdSource)) return listaAbonadoIdSource.value;
        if (typeof listaAbonadoIdSource === 'function') return listaAbonadoIdSource();
        return listaAbonadoIdSource;
    };

    // ─── Estado compartido del stepper ────────────────────────────────────
    const pasoActual = ref(PASO_ZONA);

    /**
     * Resultado del pago Paycomet (`get-pedido-procesado-paycomet`) recibido
     * al emitir `completado` desde el paso de pago. Se guarda aquí para el
     * paso post-pago (PASO_PAGO_CONFIRMACION) que muestra el resumen del
     * cambio y el nº de pedido. `null` hasta que el pago finaliza.
     * @type {import('vue').Ref<Object|null>}
     */
    const pagoResultado = ref(null);

    /**
     * Tipo de SVG del recinto (1 = asientos normales, 2 = Leaflet 3D).
     * Se establece al llamar a iniciar() desde la vista padre.
     * @type {import('vue').Ref<number|null>}
     */
    const tipoSvg = ref(null);

    /**
     * Tipo de proceso online del recinto (1 = regiones, 2 = secciones,
     * 3 = mapa general, null = fallback-legacy). Se establece al llamar
     * a iniciar() desde la vista padre.
     * @type {import('vue').Ref<number|null>}
     */
    const tipoProcesoOnline = ref(null);

    /**
     * Dispatcher de camino UI basado en `tipo_proceso_online`.
     * Replica el dispatcher canónico de ProcesoCompraAbonadoNormal.vue:70-83.
     * @type {ReturnType<typeof useCambioButacaDispatcher>}
     */
    const dispatcher = useCambioButacaDispatcher(tipoProcesoOnline);

    /**
     * Detalle completo del abono (asiento actual, precios, etc.).
     * Se declara arriba (antes de los sub-composables) para poder inyectarse
     * en `useCambioButacaButacas` y permitirle derivar su propio filtro
     * por `descuento.renovacion` de forma reactiva.
     * Se usa en el paso de confirmación para mostrar la butaca antigua.
     */
    const abonoDetail = ref(null);

    // ─── Composición de sub-composables ───────────────────────────────────
    const zonas         = useCambioButacaZonas();
    const butacas       = useCambioButacaButacas(abonoDetail);
    const confirmacion  = useCambioButacaConfirmacion();

    /**
     * ID del recinto. Se almacena cuando se llama a iniciar(recintoId)
     * y se usa al cargar butacas (nextStep / prevStep).
     * @type {import('vue').Ref<string|number|null>}
     */
    const recintoIdRef = ref(null);

    // ─── Datos del abono ───────────────────────────────────────────────────
    const tipoAbonoId = ref(null);
    const descuentoId = ref(null);

    // ─── Tipo de abono (selector para butaca nueva) ─────────────────────────
    /** Precios crudos devueltos por la butaca seleccionada (sin filtrar). */
    const preciosRaw = ref([]);
    const tipoAbonoSeatId = ref(null);

    /**
     * ¿El descuento del abonado es de renovación?
     * `null` mientras `abonoDetail` no ha cargado → no filtra hasta entonces
     * para no esconder opciones durante la carga inicial.
     */
    const abonadoEsRenovacion = computed(() => {
        const renov = abonoDetail.value?.descuento?.renovacion;
        return renov != null ? Boolean(renov) : null;
    });

    /**
     * Precios disponibles filtrados por `abonadoEsRenovacion`.
     * Si el descuento no está cargado todavía (`null`), devuelve el array crudo.
     * Así el `<select>` muestra todo hasta que el detalle confirma la clase.
     */
    const preciosDisponibles = computed(() => {
        if (!preciosRaw.value.length) return [];
        if (abonadoEsRenovacion.value === null) return preciosRaw.value;
        return preciosRaw.value.filter(
            p => Boolean(p.renovacion) === abonadoEsRenovacion.value
        );
    });

    const tipoAbonoNuevoDescripcion = computed(() => {
        if (!tipoAbonoSeatId.value) return null;
        // Fuente 1: precios de la butaca seleccionada (Leaflet tipo_svg=2,
        // mapa-general, secciones). En esos caminos el selector emite
        // `precio.id`, así que el id vive en `preciosDisponibles`.
        const fromPrecios = preciosDisponibles.value.find(
            p => p.id === tipoAbonoSeatId.value
        );
        if (fromPrecios) return fromPrecios.descripcion ?? null;
        // Fuente 2: tipos de abono de la zona (antifluo asientos normales).
        // El selector de `CambioAsientoPasoRegiones` emite `tipoAb.id`
        // (tipos de abono de la zona), que NO vive en `preciosDisponibles`
        // (precios de la butaca) — buscar solo ahí dejaba la descripción en
        // null en el paso de confirmación.
        const fromTipos = butacas.tiposAbonosDisponibles.value.find(
            t => t.id === tipoAbonoSeatId.value
        );
        return fromTipos?.descripcion ?? null;
    });

    // Watch asientoSeleccionado — populate preciosRaw (computed deriva la lista filtrada).
    watch(() => butacas.asientoSeleccionado.value, seat => {
        preciosRaw.value = seat?.precios ?? [];
        tipoAbonoSeatId.value = null;
    });

    // Watch preciosDisponibles — auto-select único o limpiar selección inválida.
    // Reacciona tanto a la selección de butaca como a la llegada tardía
    // de `abonoDetail` (cambio de filtro → re-derivación).
    //
    // No clobberea una selección manual del usuario mientras siga siendo válida
    // dentro del nuevo filtro. Sólo se actualiza cuando la selección actual queda
    // fuera de la lista filtrada.
    watch(preciosDisponibles, list => {
        const currentId = tipoAbonoSeatId.value;
        const currentIsValid = currentId != null && list.some(p => p.id === currentId);
        if (currentIsValid) return;
        tipoAbonoSeatId.value = list.length === 1 ? list[0].id : null;
    });

    // Watch tipoAbonoSeatId — sync descuentoId from selected seat's precio
    watch(tipoAbonoSeatId, newVal => {
        if (newVal && preciosDisponibles.value.length) {
            const precio = preciosDisponibles.value.find(p => p.id === newVal);
            if (precio?.descuentos?.length) {
                descuentoId.value = precio.descuentos[0].id;
            }
        }
    });

    /**
     * `abonoDetail` ya está declarado arriba (antes de los sub-composables) para
     * inyección en `useCambioButacaButacas`. Aquí sólo añadimos los auxiliares.
     */

    /** Error al cargar el detalle del abono. */
    const abonoDetailError = ref(null);

    /** Promesa de carga del detalle del abono, para await en nextStep si es necesario. */
    let abonoDetailPromise = null;

    /**
     * Carga el detalle del abono para extraer tipo_abono_id (precio.id),
     * descuento_id (descuento.id) y el asiento actual del abonado.
     *
     * Normaliza la respuesta del backend manejando distintos niveles de anidamiento
     * (API puede devolver { data: { asiento, precio } } o { abonado: { asiento, precio } }).
     */
    const loadAbonoDetail = async() => {
        const abonadoId = getAbonadoId();
        if (!abonadoId) return;
        try {
            const detalle = await abonosService.getAbonadoDetalle(abonadoId);
            // Normalizar: detectar si ya está plano (asiento/plaza en raíz)
            // o si viene envuelto en abonado{} o data{}
            const normalized = (detalle?.asiento || detalle?.plaza)
                ? detalle
                : (detalle?.abonado ?? detalle?.data ?? detalle);
            abonoDetail.value = normalized;
            tipoAbonoId.value = normalized?.precio?.id ?? null;
            descuentoId.value = normalized?.descuento?.id ?? null;
            abonoDetailError.value = null;
        } catch (err) {
            abonoDetailError.value = err?.message
                ?? (process.env.NODE_ENV !== 'production' ? String(err) : null);
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButaca] Error al cargar detalle del abono:', err);
            }
        }
    };

    // ─── Inicialización ───────────────────────────────────────────────────

    /**
     * Punto de entrada del stepper. Resetea el estado, posiciona en PASO_ZONA.
     * La carga de secciones/zonas la maneja SeccionesComponent internamente
     * via store dispatch.
     *
     * Normalización de entradas (single normalization point):
     *  - `recintoTipoSvg` y `recintoTipoProcesoOnline` se normalizan a `Number`
     *    AQUÍ, no en cada sitio de uso. Aceptan strings numéricos (`'1'`, `'2'`)
     *    o números (`1`, `2`); tras la normalización `tipoSvg` queda en
     *    `{1, 2, null}` y `tipoProcesoOnline` en `{1, 2, 3, null}`.
     *  - Valores inválidos (NaN, out-of-range, `null`, `undefined`) caen a
     *    `null` para que el resto del composable compare siempre con
     *    `=== 1` / `=== 2` sin falsos negativos por coerción de tipo.
     *  - Camino `secciones` (proceso=2): `SeccionesComponent` de master
     *    espera `tipo_svg=2`. Si la fuente entrega `null` para `tipo_svg`
     *    en proceso 2, forzamos `tipoSvg = 2` para que la sección
     *    renderice correctamente.
     *
     * @param {string|number} [recintoId]            ID del recinto
     * @param {number|string|null} [recintoTipoSvg]  Tipo de SVG del recinto (1=asientos normales, 2=Leaflet 3D)
     * @param {number|string|null} [recintoTipoProcesoOnline] Tipo de proceso online del recinto (1=regiones, 2=secciones, 3=mapa general, null=fallback-legacy)
     */
    const iniciar = async(recintoId, recintoTipoSvg, recintoTipoProcesoOnline) => {
        const abonadoId = getAbonadoId();
        if (!abonadoId) return;

        _resetAll();

        // Normalización única. Cualquier string numérico / number / null /
        // undefined se mapea a su valor canónico o a `null` si no es válido.
        const tipoSvgRaw = Number(recintoTipoSvg);
        const tipoProcesoRaw = Number(recintoTipoProcesoOnline);
        const tipoSvgNorm = (tipoSvgRaw === 1 || tipoSvgRaw === 2) ? tipoSvgRaw : null;
        const tipoProcesoNorm = (tipoProcesoRaw === 1 || tipoProcesoRaw === 2 || tipoProcesoRaw === 3)
            ? tipoProcesoRaw
            : null;
        // Camino secciones (proceso=2): SeccionesComponent de master espera
        // tipo_svg=2. Si la fuente entrega null/undefined para tipo_svg en
        // proceso 2, forzamos tipoSvg=2 para que la sección renderice.
        const tipoSvgFinal = (tipoProcesoNorm === 2 && tipoSvgNorm === null) ? 2 : tipoSvgNorm;

        tipoSvg.value = tipoSvgFinal;
        tipoProcesoOnline.value = tipoProcesoNorm;
        recintoIdRef.value = recintoId ?? null;

        // Camino 'mapa-general' salta el paso de zona: el mapa se muestra
        // directamente en PASO_BUTACA. Los demás caminos inician en PASO_ZONA.
        if (dispatcher.camino.value === 'mapa-general') {
            pasoActual.value = PASO_BUTACA;
        } else {
            pasoActual.value = PASO_ZONA;
        }

        // Cargar zonas planas en DOS escenarios:
        //  - `tipo_svg === 1` (asientos normales): precargar zonas + SVG recinto
        //    para VenueMapCard / ZoneTableCard.
        //  - `tipo_svg !== 1 && tipo_proceso_online === null` (fallback-legacy):
        //    `CambioAsientoPasoZona` renderiza en modo flat-zones; sin cargar las
        //    zonas el array queda vacío y `puedeAvanzarPasoZona` (que requiere
        //    `zonaSeleccionada`) bloquea el avance. En este caso NO se llama a
        //    `cargarSvgRecinto`: solo aplica a `tipo_svg === 1`.
        // Se pasan listaAbonadoId, hashTeatro y renovacion para que `cargarZonas`
        // recupere también las REGIONES ANEXAS
        // (`/f/temporadas/{lista}/recintos/{recinto}/zonas/anexas`) y las unifique
        // con las zonas normales.
        if (tipoSvg.value === 1 || (tipoSvg.value !== 1 && tipoProcesoOnline.value === null)) {
            const renov = abonadoEsRenovacion.value === true;
            await zonas.cargarZonas(
                abonadoId,
                recintoId,
                getListaAbonadoId(),
                localStorage.getItem('hash_teatro'),
                renov
            );
            if (tipoSvg.value === 1) {
                await zonas.cargarSvgRecinto(recintoId);
            }
        }
        // Para tipo_svg=2 (Leaflet 3D), SeccionesComponent maneja todo internamente via store.

        // Para camino 'mapa-general', cargar el mapa del recinto directamente.
        // IMPORTANTE: el detalle del abono debe cargarse ANTES de pedir las
        // butacas para poder pasar el flag `renovacion` correcto al backend.
        // Si se pide con renovacion=false, el backend devuelve TODOS los
        // precios (normal + renovación) y luego el computed `preciosDisponibles`
        // los filtra en el cliente — inconsistente con el camino secciones,
        // donde el backend ya devuelve solo los precios que aplican y el
        // selector de tipo de abono aparece cuando hay 2 opciones válidas.
        if (dispatcher.camino.value === 'mapa-general') {
            // Activar el skeleton de carga desde el PRIMER frame. Sin esto, el
            // await de `loadAbonoDetail()` (necesario para el flag `renovacion`)
            // deja un render intermedio con `loadingAsientos=false` y datos
            // vacíos, y la vista pinta el estado vacío "no hay butacas" antes
            // de que `cargarMapaGeneral` active el loading. `cargarMapaGeneral`
            // vuelve a setear el flag (idempotente) y lo apaga en `finally`.
            butacas.loadingAsientos.value = true;
            abonoDetailPromise = loadAbonoDetail();
            await abonoDetailPromise;
            const renovMapa = abonadoEsRenovacion.value === true;
            await butacas.cargarMapaGeneral(
                abonadoId,
                recintoIdRef.value,
                getListaAbonadoId(),
                localStorage.getItem('hash_teatro'),
                renovMapa
            );
        } else {
            abonoDetailPromise = loadAbonoDetail();
        }
    };

    // ─── Navegación ───────────────────────────────────────────────────────

    /**
     * Avanza al siguiente paso con los efectos secundarios necesarios.
     */
    const nextStep = async() => {
        const abonadoId = getAbonadoId();

        if (pasoActual.value === PASO_ZONA) {
            if (!zonas.puedeAvanzarPasoZona.value) return;
            pasoActual.value = PASO_BUTACA;
            const zonaId = zonas.zonaSeleccionada.value.id;
            const hashTeatro = localStorage.getItem('hash_teatro');
            // Si el descuento del abonado es de renovación, lo mandamos al
            // backend para que filtre los precios en la respuesta.
            const renov = abonadoEsRenovacion.value === true;
            await butacas.cargarAsientos(
                abonadoId,
                zonaId,
                recintoIdRef.value,
                getListaAbonadoId(),
                hashTeatro,
                tipoSvg.value,
                tipoProcesoOnline.value,
                renov,
                // Plazas per-zona: para tipo_svg=1 + proceso=1 la zona con
                // plazas usa SVG de región + butacas (Leaflet); sin plazas
                // usa el grid antifluo. Normalizamos con Boolean() porque el
                // backend puede enviar 0/1 o true/false (patrón reserva L337).
                Boolean(zonas.zonaSeleccionada.value?.tiene_plazas)
            );
            return;
        }

        if (pasoActual.value === PASO_BUTACA) {
            if (!puedeAvanzarPasoButaca.value) return;

            // Asegurar que el detalle del abono está cargado
            if (abonoDetailPromise) {
                await abonoDetailPromise;
            }

            const zonaId = zonas.zonaSeleccionada.value?.id ?? null;
            const as = butacas.asientoSeleccionado.value;

            // Defensa per-seat: si la zona no marca `tiene_plazas=true` pero el seat
            // individual tiene `es_plaza=true`, ascender a Camino 2 y avisar en dev.
            // Esto blinda el caso "flag por zona ausente pero flag per-seat presente".
            const esPlazaPorZona = Boolean(zonas.zonaSeleccionada.value?.tiene_plazas);
            const esPlazaPorSeat = Boolean(as?.es_plaza);
            const esPlaza = esPlazaPorZona || esPlazaPorSeat;

            if (esPlazaPorSeat && !esPlazaPorZona && process.env.NODE_ENV !== 'production') {
                console.warn(
                    '[useCambioButaca] Defensa per-seat: la zona no marca `tiene_plazas=true` pero el seat tiene `es_plaza=true`. '
                    + 'Asciende a Camino 2 (plaza_id). Esto puede indicar inconsistencia entre el flag per-zona y per-seat.'
                );
            }

            // Deriva el tipo_abono_id correcto para el backend:
            //  - Old endpoint (tipo_svg=1): precio.id ≠ precio_lista_abonado_id → usar este último
            //  - Temporada (tipo_svg=2):    precio.id = precio_lista_abonado_id → usar precio.id
            const selectedPrecio = preciosDisponibles.value.find(
                p => p.id === tipoAbonoSeatId.value
            );
            const tipoAbonoIdForBackend = selectedPrecio?.precio_lista_abonado_id
                ?? tipoAbonoSeatId.value
                ?? tipoAbonoId.value;

            const seatData = {
                tipo_abono_id: tipoAbonoIdForBackend,
            };

            if (dispatcher.camino.value === 'mapa-general') {
                // Camino mapa-general (tipo_proceso_online=3): el flujo inicia
                // directamente en PASO_BUTACA con el mapa del recinto y NO hay
                // zona seleccionada (zonaId === null). El backend identifica la
                // butaca por su id global `plaza_id` (= `b.id` del mapa), que el
                // service propaga SIEMPRE. Nunca se envían zona_id/region_id/
                // row/seat aquí: region_id sería `Number(null) = 0` y row/seat
                // son meramente nominales en el mapa del recinto.
                seatData.plaza_id = Number(as?.plaza_id ?? as?.id);
            } else if (!esPlaza) {
                // Camino 1: Butaca numerada
                seatData.zona_id   = zonaId;
                seatData.region_id = Number(zonaId);
                seatData.row       = Number(as?.row ?? as?.fila);
                seatData.seat      = Number(as?.number ?? as?.asiento);
            } else {
                // Camino 2: Plaza (tiene_plazas = true)
                seatData.plaza_id  = Number(as?.plaza_id ?? as?.id);
            }

            try {
                await confirmacion.reservar(abonadoId, seatData);
                pasoActual.value = PASO_CONFIRMACION;
            } catch (_) {
                // Error ya manejado dentro de confirmacion.reservar
                // El usuario permanece en PASO_BUTACA
            }
            return;
        }

        // PASO_CONFIRMACION: la confirmación se hace explícitamente con confirmarCambio().
        // Si requiere pago y el backend devolvió `jet_id` (pasarela Paycomet), la
        // vista padre llama a `nextStep` para avanzar a PASO_PAGO. El TPV Redsys
        // (`formulario`) NO avanza: su form se auto-submitea desde la confirmación.

        if (pasoActual.value === PASO_CONFIRMACION) {
            if (confirmacion.jetId.value) {
                pasoActual.value = PASO_PAGO;
            }
            return;
        }
    };

    /**
     * Flag de loading durante la ejecución de `prevStep`. Se expone para que la
     * vista pueda deshabilitar el botón "Atrás" mientras está en vuelo (incluido
     * el HTTP `liberarSesion` cuando se retrocede desde `PASO_CONFIRMACION`) y
     * así impedir que el usuario spamee el botón generando múltiples llamadas
     * al backend.
     */
    const loadingPrevStep = ref(false);

    /**
     * Retrocede al paso anterior con los efectos secundarios necesarios.
     */
    const prevStep = async() => {
        if (loadingPrevStep.value) return;
        loadingPrevStep.value = true;
        try {
            await _prevStepInternal();
        } finally {
            loadingPrevStep.value = false;
        }
    };

    const _prevStepInternal = async() => {
        const abonadoId = getAbonadoId();

        if (pasoActual.value === PASO_ZONA) {
            // No hay paso anterior a ZONA; el botón Atrás se oculta.
            return;
        }

        if (pasoActual.value === PASO_BUTACA) {
            // En el camino 'mapa-general' el flujo inicia directamente en
            // PASO_BUTACA: no hay PASO_ZONA previo al que volver (el botón
            // Atrás se oculta en la vista). Blindaje por si `prevStep` se
            // invoca programáticamente: retroceder dejaría la pantalla en
            // blanco porque el dispatcher no renderiza PASO_ZONA en este camino.
            if (dispatcher.camino.value === 'mapa-general') {
                return;
            }
            // Vaciar la selección de butaca al retroceder (mismo patrón que
            // invitaciones): la zona se limpia y la tabla de butacas
            // seleccionadas queda vacía.
            butacas.quitarAsiento();
            zonas.limpiarZona();
            pasoActual.value = PASO_ZONA;
            return;
        }

        if (pasoActual.value === PASO_CONFIRMACION) {
            // Vaciar la selección de butaca AL RETROCEDER. En el camino
            // 'mapa-general' (tipo_proceso_online=3) `cargarAsientos` retorna
            // temprano porque no hay zonaId y por tanto NO limpia la selección
            // (su limpieza vive después del guard); sin esta llamada explícita
            // la tabla de butacas seleccionadas seguiría mostrando la butaca
            // elegida antes de confirmar.
            butacas.quitarAsiento();
            await confirmacion.liberarSesion(abonadoId);
            confirmacion.reset();
            pasoActual.value = PASO_BUTACA;
            const hashTeatro = localStorage.getItem('hash_teatro');
            const renov = abonadoEsRenovacion.value === true;
            await butacas.cargarAsientos(
                abonadoId,
                zonas.zonaSeleccionada.value?.id,
                recintoIdRef.value,
                getListaAbonadoId(),
                hashTeatro,
                tipoSvg.value,
                tipoProcesoOnline.value,
                renov,
                Boolean(zonas.zonaSeleccionada.value?.tiene_plazas)
            );
            return;
        }

        if (pasoActual.value === PASO_PAGO) {
            // Volvemos al paso de confirmación SIN liberar la sesión: el usuario
            // puede revisar los datos y reintentar. La sesión sigue siendo válida
            // porque sólo se libera en PASO_CONFIRMACION → PASO_BUTACA.
            pasoActual.value = PASO_CONFIRMACION;
            return;
        }

        if (pasoActual.value === PASO_PAGO_CONFIRMACION) {
            // El pago ya se ha completado: no tiene sentido retroceder (volver
            // al paso de pago re-montaría un TPV ya consumido). El flujo se
            // cierra con el botón "Finalizar" de este paso.
            return;
        }
    };

    // ─── Confirmación final ───────────────────────────────────────────────

    /**
     * Registra la finalización del pago Paycomet y avanza al paso post-pago.
     *
     * `PagoPaycometComponent` emite `completado` con la respuesta de
     * `get-pedido-procesado-paycomet`. En lugar de cerrar el flujo (como hace
     * la redirección legacy), guardamos el resultado y avanzamos a
     * PASO_PAGO_CONFIRMACION, donde la vista muestra el resumen del cambio.
     * El cierre real del flujo lo hace el botón "Finalizar" de ese paso.
     *
     * @param {Object} [result] Payload emitido por el pago (n_pedido, error).
     */
    const registrarPagoCompletado = result => {
        pagoResultado.value = result ?? null;
        pasoActual.value = PASO_PAGO_CONFIRMACION;
    };

    /**
     * Avanza al paso de resumen de compra (PASO_PAGO_CONFIRMACION) para un
     * cambio SIN coste (precio_total === 0). No hay pago que registrar, así que
     * `pagoResultado` queda en `null` y la vista muestra igualmente el resumen
     * final (con el banner de "cambio exitoso" en lugar del de pago).
     * El cierre real del flujo lo hace el botón "Finalizar" de ese paso
     * (emite `completado` al padre desde la vista).
     */
    const registrarCambioGratuito = () => {
        pagoResultado.value = null;
        pasoActual.value = PASO_PAGO_CONFIRMACION;
    };

    /**
     * Ejecuta la confirmación del cambio de butaca.
     * Si `requierePago` es true, la vista padre debe mostrar el PaymentModal
     * en lugar de llamar a este método.
     *
     * @returns {Promise<boolean>} `true` si el cambio se confirmó correctamente
     */
    const confirmarCambio = async() => {
        if (!confirmacion.puedeConfirmar.value) return false;

        const abonadoId = getAbonadoId();
        const success = await confirmacion.confirmar(abonadoId);

        if (success) {
            confirmacion.limpiarSessionId();
        }

        return success;
    };

    /**
     * Carga secciones (camino 'secciones', tipo_proceso_online=2).
     *
     * En la implementación actual, `SeccionesComponent` maneja su propia carga
     * internamente via store dispatch en su `created()`. Este método queda
     * como hook de orquestación para futuras integraciones (e.g. carga
     * bajo demanda o pre-fetch). Devuelve `void` para mantener contrato
     * uniforme con `cargarZonas` / `cargarAsientos`.
     *
     * @returns {Promise<void>}
     */
    const cargarSecciones = async() => {
        // SeccionesComponent se auto-carga; este hook queda como punto
        // de extensión para pre-fetch o para integrar la carga fuera del
        // componente si fuera necesario en el futuro.
        return Promise.resolve();
    };

    /**
     * Carga el mapa general del recinto (camino 'mapa-general',
     * `tipo_proceso_online=3`). No requiere zona: delega en
     * `useCambioButacaButacas.cargarMapaGeneral` que carga SVG + butacas.
     *
     * @param {string|number} recintoId
     * @returns {Promise<void>}
     */
    const cargarMapaGeneral = async recintoId => {
        const abonadoId = getAbonadoId();
        if (!abonadoId || !recintoId) return;
        await butacas.cargarMapaGeneral(
            abonadoId,
            recintoId,
            getListaAbonadoId(),
            localStorage.getItem('hash_teatro')
        );
    };

    // ─── Cancelación ──────────────────────────────────────────────────────

    /**
     * Cancela el flujo completo. Libera la sesión si existe y resetea todo.
     */
    const cancelar = async() => {
        await confirmacion.liberarSesion(getAbonadoId());
        _resetAll();
    };

    // ─── Estado de navegación ─────────────────────────────────────────────

    // Exponer `loadingPrevStep` (declarado arriba, junto a `prevStep`).

    // ─── Timer externo ────────────────────────────────────────────────────

    /**
     * Callback que debe llamar el componente CountdownBar cuando el timer llega a 0.
     */
    const onTimerExpirado = async() => {
        await confirmacion.onTimerExpirado();
        _resetAll();
    };

    // ─── Limpieza de sesión (beforeunload) ────────────────────────────────

    /**
     * Libera la sesión activa. Debe llamarse desde el evento `beforeunload`
     * de la ventana para evitar butacas huérfanas.
     */
    const liberarSesion = () => confirmacion.liberarSesion(getAbonadoId());

    /**
     * Versión `keepalive` de la liberación de sesión. Pensada para el
     * handler `beforeunload`: dispara un `fetch` con `keepalive: true`
     * que sobrevive al cierre de la pestaña.
     *
     * Acepta un `sessionIdOverride` para que el caller (la vista) pueda
     * pasar el sessionId actual sin esperar a que `sessionId.value` se
     * consulte (el composable también lo lee si no se pasa).
     *
     * @param {string|null} [sessionIdOverride=null]
     */
    const liberarSesionKeepalive = sessionIdOverride => confirmacion.liberarSesionKeepalive(sessionIdOverride);

    // ─── Reset completo ───────────────────────────────────────────────────

    /**
     * `puedeAvanzarPasoButaca` — versión orquestadora que cubre 2 ramas:
     *
     *  - Zona con PLAZAS (`tiene_plazas === true`): hay un asiento seleccionado.
     *  - Zona con BUTACAS NUMERADAS (resto): hay un asiento seleccionado.
     *
     * En ambas se exige `asientoSeleccionado` y, si la butaca tiene más de un
     * tipo de abono (`preciosDisponibles.length > 1`), una selección explícita
     * de `tipoAbonoSeatId`. Este computed SOBREESCRIBE la versión del
     * sub-composable para la vista.
     */
    const puedeAvanzarPasoButaca = computed(() => {
        if (!butacas.asientoSeleccionado.value) return false;
        // Si hay más de 1 tipo de abonos, requerir selección explícita.
        // (Con 1 tipo, el watch auto-selecciona; con 0, no se puede avanzar.)
        if (preciosDisponibles.value.length > 1 && !tipoAbonoSeatId.value) return false;
        return true;
    });

    const _resetAll = () => {
        pasoActual.value = PASO_ZONA;
        pagoResultado.value = null;
        tipoAbonoId.value = null;
        descuentoId.value = null;
        abonoDetail.value = null;
        abonoDetailError.value = null;
        abonoDetailPromise = null;
        _resetPrecios();
        zonas.reset();
        butacas.reset();
        confirmacion.reset();
    };

    const _resetPrecios = () => {
        // Resetear `preciosRaw` (ref fuente), NUNCA `preciosDisponibles` (computed).
        // Asignar a un computed dispara Vue warning "computed value is readonly".
        preciosRaw.value = [];
        tipoAbonoSeatId.value = null;
    };

    // ─── API pública ──────────────────────────────────────────────────────
    return {
        // Constantes
        PASO_ZONA,
        PASO_BUTACA,
        PASO_CONFIRMACION,
        PASO_PAGO,
        PASO_PAGO_CONFIRMACION,

        // Navegación
        pasoActual,
        pagoResultado,

        // Tipo de SVG del recinto
        tipoSvg,

        // Tipo de proceso online del recinto
        tipoProcesoOnline,

        // Camino UI derivado del `tipo_proceso_online` (regiones|secciones|mapa-general|fallback-legacy)
        camino:                dispatcher.camino,

        // PASO_ZONA (delegado a zonas sub-composable)
        zonas:             zonas.zonas,
        loadingZonas:      zonas.loadingZonas,
        errorZonas:        zonas.errorZonas,
        zonaSeleccionada:  zonas.zonaSeleccionada,
        svgRecinto:        zonas.svgRecinto,
        isZonasEmpty:      zonas.isZonasEmpty,

        // PASO_BUTACA (delegado a butacas sub-composable)
        asientosData:         butacas.asientosData,
        svgString:            butacas.svgString,
        asientosParaPicker:   butacas.asientosParaPicker,
        loadingAsientos:      butacas.loadingAsientos,
        errorAsientos:        butacas.errorAsientos,
        asientoSeleccionado:  butacas.asientoSeleccionado,
        tiposAbonosDisponibles: butacas.tiposAbonosDisponibles,
        isAsientosEmpty:      butacas.isAsientosEmpty,

        // PASO_CONFIRMACION (delegado a confirmacion sub-composable)
        sessionId:             confirmacion.sessionId,
        sessionEndsAt:         confirmacion.sessionEndsAt,
        tipoAbonoId,
        loadingReservar:       confirmacion.loadingReservar,
        errorReservar:         confirmacion.errorReservar,
        loadingConfirmar:      confirmacion.loadingConfirmar,
        errorConfirmar:        confirmacion.errorConfirmar,
        precioTotal:           confirmacion.precioTotal,
        importe:               confirmacion.importe,
        importeCambio:         confirmacion.importeCambio,
        priceManagement:       confirmacion.priceManagement,
        mostrarGastosGestion:  confirmacion.mostrarGastosGestion,
        jetId:                 confirmacion.jetId,
        formulario:            confirmacion.formulario,
        priceManagementInterno: confirmacion.priceManagementInterno,
        abonadoEsRenovacion:   abonadoEsRenovacion,
        abonoDetail:           abonoDetail,
        abonoDetailError:      abonoDetailError,
        preciosDisponibles:    preciosDisponibles,
        tipoAbonoSeatId:       tipoAbonoSeatId,
        tipoAbonoNuevoDescripcion: tipoAbonoNuevoDescripcion,
        resultadoCambio:       confirmacion.resultadoCambio,
        requierePago:          confirmacion.requierePago,
        puedeConfirmar:        confirmacion.puedeConfirmar,
        // Info post-pago
        resultadoInfo:         confirmacion.resultadoInfo,
        loadingInfo:           confirmacion.loadingInfo,

        // Guards cross-step
        puedeAvanzarPasoZona:   zonas.puedeAvanzarPasoZona,
        puedeAvanzarPasoButaca: puedeAvanzarPasoButaca,

        // Métodos: inicialización
        iniciar,

        // Métodos: PASO_ZONA
        seleccionarZona: zonas.seleccionarZona,
        limpiarZona:     zonas.limpiarZona,
        cargarSecciones,

        // Métodos: PASO_BUTACA
        toggleAsiento: butacas.toggleAsiento,
        quitarAsiento: butacas.quitarAsiento,
        cargarMapaGeneral,

        // Métodos: PASO_CONFIRMACION
        confirmarCambio,
        consultarInfo:         confirmacion.consultarInfo,

        // Métodos: navegación
        nextStep,
        prevStep,
        loadingPrevStep,
        cancelar,
        registrarPagoCompletado,
        registrarCambioGratuito,

        // Métodos: timer + sesión
        onTimerExpirado,
        liberarSesion,
        liberarSesionKeepalive,
    };
}
